<script setup>
  import {computed, ref, watch} from "vue";

  import {useForm, usePage} from "@inertiajs/vue3";
  import {pcneStructure} from "@pages/Records/Components/Result/Enums.js";
  import {MedInfoButton, MedLink} from "@pharma4u/medicheck-components";
  import {debounce} from "es-toolkit";

  defineOptions({
    name: "PagesRecordResultListClaimInteractionDocumentation",
  });

  const props = defineProps({
    mcCheckId: {
      type: Number,
      required: true,
    },
    claim: {
      type: Object,
      required: true,
    },
    isRecordWithPcne: {
      type: Boolean,
      default: false,
    },
  });

  const page = usePage();

  const medications = ref(page.props?.medications?.data ?? []);

  watch(
    () => page.props.medications,
    (newValue) => {
      medications.value = (newValue?.data ?? [])
        .filter((medication) => medication.abdata_key_fam !== null && medication.abdata_key_fam !== 0)
        .map((medication) => {
          return {abdata_key_fam: medication.abdata_key_fam, fam: medication.fam};
        });
    },
    {deep: true},
  );

  const form = useForm({
    problem_code: props.claim?.pcne?.problem_code ?? defaultProblemCode(),
    cause_code: props.claim?.pcne?.cause_code ?? defaultCauseCode(),
    communication_code: props.claim?.pcne?.communication_code ?? null,
    intervention_code: props.claim?.pcne?.intervention_code ?? null,
    intervention_text: props.claim?.pcne?.intervention_text ?? null,
    acceptance_code: props.claim?.pcne?.acceptance_code ?? null,
    acceptance_text: props.claim?.pcne?.acceptance_text ?? null,
    status_code: props.claim?.pcne?.status_code ?? null,
    status_text: props.claim?.pcne?.status_text ?? null,
    pcnefams: props.claim?.pcne?.pcnefams ?? [],
  });

  watch(
    () => [
      form.problem_code,
      form.cause_code,
      form.communication_code,
      form.intervention_code,
      form.intervention_text,
      form.acceptance_code,
      form.acceptance_text,
      form.status_code,
      form.status_text,
      form.pcnefams,
    ],
    debounce(function () {
      save();
    }, 1000),
    {flush: "post"},
  );

  function save() {
    // update
    if (props.claim?.pcne?.id) {
      form.put(
        route("pcnes.update", {
          patient: page.props.patient.id,
          record: page.props.record.id,
          check: props.claim.check_id,
          claim: props.claim.id,
          pcne: props.claim.pcne.id,
        }),
        {
          preserveScroll: true,
          only: ["flash", "errors", "record", "checks"],
        },
      );
    }
    // create
    else {
      form.post(
        route("pcnes.store", {
          patient: page.props.patient.id,
          record: page.props.record.id,
          check: props.claim.check_id,
          claim: props.claim.id,
        }),
        {
          preserveScroll: true,
          preserveState: true,
          only: ["flash", "errors", "record", "checks"],
        },
      );
    }
  }

  const pcneOptions = computed(() => {
    return pcneStructure[props.claim?.pcne?.version || "9.0"];
  });

  function defaultProblemCode() {
    if (!props.isRecordWithPcne) {
      return null;
    }

    return (
      pcneStructure[props.claim?.pcne?.version || "9.0"].defaultValues.find(
        (value) => value.mcCheckId === props.mcCheckId,
      )?.pCode || null
    );
  }

  function defaultCauseCode() {
    if (!props.isRecordWithPcne) {
      return null;
    }

    return (
      pcneStructure[props.claim?.pcne?.version || "9.0"].defaultValues.find(
        (value) => value.mcCheckId === props.mcCheckId,
      )?.cCode || null
    );
  }
</script>

<template>
  <div class="text-sm">
    <div class="flex items-center space-x-2">
      <h6 class="text-sm font-semibold">Dokumentation nach PCNE</h6>
      <MedInfoButton
        size="lg"
        side="top"
        aria-label="PCNE Info"
        :data-test="getTestId('interaction-documentation-pcne-info-button')"
      >
        <p class="max-w-sm text-sm">
          Dokumentation modifiziert nach PCNE =
          <MedLink
            variant="primary"
            href="https://www.pcne.org/working-groups/2/drug-related-problem-classification"
            target="_blank"
            :data-test="getTestId('interaction-link')"
          >
            Pharmaceutical Care Network Europe.
          </MedLink>
        </p>
        <p class="mt-2 max-w-sm text-sm">
          Die Dokumentation nach PCNE wurde im MediCheck eingestellt. Vorhandene Dokumentation aus früheren Analysen
          wird Ihnen noch angezeigt, kann aber nicht mehr bearbeitet werden.
        </p>
      </MedInfoButton>
    </div>

    <ul class="mt-2 flex flex-col space-y-4">
      <li v-if="form.problem_code !== null">
        <div class="text-gray-500">
          {{ "Problem" + (form.problem_code ? " (" + form.problem_code + ")" : "") }}
        </div>
        <div>{{ pcneOptions["problems"]?.find((problem) => problem.code === form.problem_code)?.text }}</div>
      </li>

      <li v-if="form.cause_code !== null">
        <div class="text-gray-500">
          {{ "Grund" + (form.cause_code ? " (" + form.cause_code + ")" : "") }}
        </div>
        <div>{{ pcneOptions["causes"]?.find((cause) => cause.code === form.cause_code)?.text }}</div>
      </li>

      <li v-if="form.problem_code !== null || form.cause_code !== null">
        <div class="text-gray-500">Welche FAM?</div>
        <div v-if="form.pcnefams && form.pcnefams.length > 0">
          {{
            form.pcnefams
              .map((pcnefam) => {
                return medications.find((medication) => medication.abdata_key_fam === pcnefam.abdata_key_fam)?.fam;
              })
              .join(", ")
          }}
        </div>
        <div v-else>&mdash;</div>
      </li>

      <li v-if="form.communication_code !== null && form.communication_code !== ''">
        <div class="text-gray-500">
          {{ "Kommunikation" + (form.communication_code ? " (" + form.communication_code + ")" : "") }}
        </div>
        <div>
          {{
            pcneOptions["communications"]?.find((communication) => communication.code === form.communication_code)?.text
          }}
        </div>
      </li>

      <li v-if="form.communication_code !== null && form.communication_code !== ''">
        <div class="text-gray-500">
          {{ "Plan" + (form.intervention_code ? " (" + form.intervention_code + ")" : "") }}
        </div>
        <div>
          {{ pcneOptions["interventions"]?.find((intervention) => intervention.code === form.intervention_code)?.text }}
        </div>

        <div
          v-if="
            form.intervention_text !== null || (form.intervention_code !== null && form.intervention_code !== 'I4.2')
          "
          class="italic"
        >
          {{ form.intervention_text }}
        </div>
      </li>

      <li v-if="form.intervention_code !== null && form.intervention_code !== ''">
        <div class="text-gray-500">
          {{ "Akzeptanz" + (form.acceptance_code ? " (" + form.acceptance_code + ")" : "") }}
        </div>
        <div>
          {{ pcneOptions["acceptances"]?.find((acceptance) => acceptance.code === form.acceptance_code)?.text }}
        </div>

        <div v-if="form.acceptance_text !== null || form.acceptance_code === 'A2.3'" class="italic">
          {{ form.acceptance_text }}
        </div>
      </li>

      <li v-if="form.acceptance_code !== null && form.acceptance_code !== ''">
        <div class="text-gray-500">
          {{ "Status" + (form.status_code ? " (" + form.status_code + ")" : "") }}
        </div>
        <div>
          {{ pcneOptions["statuses"]?.find((status) => status.code === form.status_code)?.text }}
        </div>

        <div v-if="form.status_text !== null || (form.status_code !== null && form.status_code !== '')" class="italic">
          {{ form.status_text }}
        </div>
      </li>
    </ul>
  </div>
</template>
